import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const COLORS = {
  added: '#d4edda',
  modified: '#fff3cd',
  removed: '#f8d7da'
};

/**
 * Produces a stable JSON string by recursively sorting object keys.
 * Prevents false diffs when two objects have identical values but different key order.
 */
const stableStringify = (val) => {
  if (val === null || val === undefined || typeof val !== 'object') {
    return JSON.stringify(val);
  }
  if (Array.isArray(val)) {
    return JSON.stringify(val.map(item => JSON.parse(stableStringify(item))));
  }
  const sorted = Object.keys(val).sort().reduce((acc, key) => {
    acc[key] = JSON.parse(stableStringify(val[key]));
    return acc;
  }, {});
  return JSON.stringify(sorted);
};

/**
 * Computes field-level diffs between input and output objects.
 * - Added: key in outputData but not in inputData
 * - Removed: key in inputData but not in outputData
 * - Modified: key in both but values differ (stable stringify comparison)
 * - Unchanged: key in both with same value
 */
const computeDiff = (inputData, outputData) => {
  const input = inputData || {};
  const output = outputData || {};
  const inputKeys = Object.keys(input);
  const outputKeys = Object.keys(output);

  const added = [];
  const modified = [];
  const removed = [];
  const unchanged = [];

  // Check output keys for added or modified
  for (const key of outputKeys) {
    if (!(key in input)) {
      added.push({ key, value: output[key] });
    } else if (stableStringify(input[key]) !== stableStringify(output[key])) {
      modified.push({ key, oldValue: input[key], newValue: output[key] });
    } else {
      unchanged.push({ key, value: output[key] });
    }
  }

  // Check input keys for removed
  for (const key of inputKeys) {
    if (!(key in output)) {
      removed.push({ key, value: input[key] });
    }
  }

  return { added, modified, removed, unchanged };
};

const formatValue = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const DiffHighlighter = ({ inputData, outputData }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const diff = useMemo(() => computeDiff(inputData, outputData), [inputData, outputData]);

  const noChanges =
    diff.added.length === 0 &&
    diff.modified.length === 0 &&
    diff.removed.length === 0;

  if (showRawJson) {
    return (
      <div>
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowRawJson(false)}
          >
            Show Diff View
          </button>
        </div>
        <div className="row">
          <div className="col-md-6">
            <h6>Input Data</h6>
            <SyntaxHighlighter
              language="json"
              style={docco}
              customStyle={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.85em' }}
            >
              {JSON.stringify(inputData, null, 2)}
            </SyntaxHighlighter>
          </div>
          <div className="col-md-6">
            <h6>Output Data</h6>
            <SyntaxHighlighter
              language="json"
              style={docco}
              customStyle={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.85em' }}
            >
              {JSON.stringify(outputData, null, 2)}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toggle to raw JSON */}
      <div className="mb-2">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowRawJson(true)}
        >
          Show Raw JSON
        </button>
      </div>

      {/* Summary bar */}
      <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#e9ecef', fontSize: '0.9em' }}>
        <span className="me-3">
          <strong style={{ color: '#155724' }}>{diff.added.length}</strong> added
        </span>
        <span className="me-3">
          <strong style={{ color: '#856404' }}>{diff.modified.length}</strong> modified
        </span>
        <span>
          <strong style={{ color: '#721c24' }}>{diff.removed.length}</strong> removed
        </span>
      </div>

      {/* No changes message */}
      {noChanges && (
        <div className="alert alert-secondary">
          No changes detected
        </div>
      )}

      {/* Added fields */}
      {diff.added.map((item) => (
        <div
          key={`added-${item.key}`}
          data-testid="diff-added"
          className="d-flex align-items-start p-2 mb-1 rounded"
          style={{ backgroundColor: COLORS.added }}
        >
          <strong className="me-2" style={{ minWidth: '150px' }}>{item.key}:</strong>
          <span>{formatValue(item.value)}</span>
        </div>
      ))}

      {/* Modified fields */}
      {diff.modified.map((item) => (
        <div
          key={`modified-${item.key}`}
          data-testid="diff-modified"
          className="d-flex align-items-start p-2 mb-1 rounded"
          style={{ backgroundColor: COLORS.modified }}
        >
          <strong className="me-2" style={{ minWidth: '150px' }}>{item.key}:</strong>
          <span>
            <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
              {formatValue(item.oldValue)}
            </span>
            {' → '}
            {formatValue(item.newValue)}
          </span>
        </div>
      ))}

      {/* Removed fields */}
      {diff.removed.map((item) => (
        <div
          key={`removed-${item.key}`}
          data-testid="diff-removed"
          className="d-flex align-items-start p-2 mb-1 rounded"
          style={{ backgroundColor: COLORS.removed }}
        >
          <strong className="me-2" style={{ minWidth: '150px' }}>{item.key}:</strong>
          <span style={{ textDecoration: 'line-through' }}>{formatValue(item.value)}</span>
        </div>
      ))}

      {/* Unchanged fields — collapsed by default */}
      {diff.unchanged.length > 0 && (
        <div className="mt-2">
          <button
            className="btn btn-sm btn-link p-0"
            onClick={() => setShowUnchanged(!showUnchanged)}
          >
            {showUnchanged ? 'Hide' : 'Show'} {diff.unchanged.length} unchanged field{diff.unchanged.length !== 1 ? 's' : ''}
          </button>
          {showUnchanged && (
            <div className="mt-1">
              {diff.unchanged.map((item) => (
                <div
                  key={`unchanged-${item.key}`}
                  className="d-flex align-items-start p-2 mb-1 rounded"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <strong className="me-2" style={{ minWidth: '150px' }}>{item.key}:</strong>
                  <span>{formatValue(item.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

DiffHighlighter.propTypes = {
  inputData: PropTypes.object,
  outputData: PropTypes.object,
};

export { computeDiff };
export default DiffHighlighter;
